import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { isWeekendDate, formatCalendarWeekdayLt } from '../utils/calendarDateUtils';
import useCalendarStickyTop from '../hooks/useCalendarStickyTop';

const TIME_COL_WIDTH = { xs: 40, sm: 50, md: 60 };
const DAY_COL_WIDTH = { xs: 72, sm: 100, md: 120 };
const SLOT_HEIGHT_DEFAULT = { xs: 32, sm: 28, md: 30 };
const HEADER_HEIGHT = { xs: 48, sm: 55, md: 60 };

const weekendHeaderBg = (theme) =>
  theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50';

const pickBreakpointPx = (map, isSmDown, isMdDown) =>
  isSmDown ? map.xs : isMdDown ? map.sm : map.md;

const PIN_FADE_RANGE_PX = 14;

/**
 * Booking calendar with split scroll panels (time column + day grid)
 * and flex or viewport-based height.
 */
const ScrollableBookingCalendar = ({
  calendarDates,
  weekDates,
  timeSlots,
  renderSlotCell,
  heightOffset = { xs: 320, sm: 280, md: 260 },
  slotHeight = SLOT_HEIGHT_DEFAULT,
  fillContainer = false,
  fullBleed = true,
  scrollToTodayOnMount = true
}) => {
  const dates = calendarDates || weekDates;
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isFinePointer = useMediaQuery('(pointer: fine)');
  const stickyTopPx = useCalendarStickyTop();

  const outerRef = useRef(null);
  const sentinelRef = useRef(null);
  const hScrollRef = useRef(null);
  const headerHScrollRef = useRef(null);
  const fixedHeaderHScrollRef = useRef(null);
  const vScrollRef = useRef(null);
  const timeScrollRef = useRef(null);
  const hasAlignedToTodayRef = useRef(false);

  const [pinProgress, setPinProgress] = useState(0);
  const [fixedFrame, setFixedFrame] = useState({ left: 0, width: 0 });
  const pinRafRef = useRef(0);

  const headerPinned = pinProgress > 0.02;

  const dayColPx = pickBreakpointPx(DAY_COL_WIDTH, isSmDown, isMdDown);
  const timeColPx = pickBreakpointPx(TIME_COL_WIDTH, isSmDown, isMdDown);
  const headerPx = pickBreakpointPx(HEADER_HEIGHT, isSmDown, isMdDown);
  const slotHeightPx = pickBreakpointPx(slotHeight, isSmDown, isMdDown);

  const dayNameShort = isSmDown;

  const syncHeaderHorizontalScroll = useCallback((scrollLeft) => {
    if (headerHScrollRef.current) {
      headerHScrollRef.current.scrollLeft = scrollLeft;
    }
    if (fixedHeaderHScrollRef.current) {
      fixedHeaderHScrollRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  const updateFixedFrame = useCallback(() => {
    const outer = outerRef.current;
    if (!outer) return;
    const rect = outer.getBoundingClientRect();
    setFixedFrame({ left: rect.left, width: rect.width });
  }, []);

  const updatePinProgress = useCallback(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const { bottom } = sentinel.getBoundingClientRect();
    const next = Math.max(0, Math.min(1, (stickyTopPx - bottom + PIN_FADE_RANGE_PX) / PIN_FADE_RANGE_PX));
    setPinProgress(next);
  }, [stickyTopPx]);

  const schedulePinUpdate = useCallback(() => {
    cancelAnimationFrame(pinRafRef.current);
    pinRafRef.current = requestAnimationFrame(() => {
      updatePinProgress();
      updateFixedFrame();
    });
  }, [updatePinProgress, updateFixedFrame]);

  const alignToTodayColumn = useCallback(() => {
    const el = hScrollRef.current;
    if (!el || !dates.length) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    const idx = dates.indexOf(today);
    const targetIdx = idx >= 0 ? idx : 0;
    const scrollLeft = Math.max(0, targetIdx * dayColPx);
    el.scrollLeft = scrollLeft;
    syncHeaderHorizontalScroll(scrollLeft);
  }, [dates, dayColPx, syncHeaderHorizontalScroll]);

  useEffect(() => {
    if (!scrollToTodayOnMount || hasAlignedToTodayRef.current) return;
    hasAlignedToTodayRef.current = true;
    requestAnimationFrame(() => {
      alignToTodayColumn();
      requestAnimationFrame(alignToTodayColumn);
    });
  }, [alignToTodayColumn, scrollToTodayOnMount]);

  useEffect(() => {
    const el = hScrollRef.current;
    if (!el || !isFinePointer) return;

    const onWheel = (event) => {
      const delta = event.deltaX !== 0 ? event.deltaX : event.deltaY;
      if (Math.abs(delta) < 0.5) return;
      const prev = el.scrollLeft;
      el.scrollLeft += delta;
      if (el.scrollLeft !== prev) {
        syncHeaderHorizontalScroll(el.scrollLeft);
        event.preventDefault();
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isFinePointer, syncHeaderHorizontalScroll]);

  useEffect(() => {
    schedulePinUpdate();
    window.addEventListener('scroll', schedulePinUpdate, { passive: true });
    window.addEventListener('resize', schedulePinUpdate, { passive: true });
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', schedulePinUpdate);
    visualViewport?.addEventListener('scroll', schedulePinUpdate);

    return () => {
      cancelAnimationFrame(pinRafRef.current);
      window.removeEventListener('scroll', schedulePinUpdate);
      window.removeEventListener('resize', schedulePinUpdate);
      visualViewport?.removeEventListener('resize', schedulePinUpdate);
      visualViewport?.removeEventListener('scroll', schedulePinUpdate);
    };
  }, [schedulePinUpdate, stickyTopPx]);

  useEffect(() => {
    if (!headerPinned || !hScrollRef.current) return;
    syncHeaderHorizontalScroll(hScrollRef.current.scrollLeft);
  }, [headerPinned, syncHeaderHorizontalScroll]);

  const handleBodyScroll = useCallback((event) => {
    const y = event.currentTarget.scrollTop;
    if (timeScrollRef.current) {
      timeScrollRef.current.scrollTop = y;
    }
  }, []);

  const handleHorizontalScroll = useCallback(
    (event) => {
      syncHeaderHorizontalScroll(event.currentTarget.scrollLeft);
    },
    [syncHeaderHorizontalScroll]
  );

  const frameBorderColor = theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400];
  const slotBorderColor = theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300];
  const frameBorder = `1px solid ${frameBorderColor}`;
  const slotBorder = `1px solid ${slotBorderColor}`;

  const headerCellSx = useMemo(
    () => ({
      height: headerPx,
      minHeight: headerPx,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderBottom: frameBorder,
      bgcolor: 'background.paper',
      flexShrink: 0
    }),
    [headerPx, frameBorder]
  );

  const dayDividerSx = useMemo(
    () => ({
      borderRight: frameBorder
    }),
    [frameBorder]
  );

  const timeColumnDividerSx = useMemo(
    () => ({
      borderRight: frameBorder
    }),
    [frameBorder]
  );

  const headerBandSx = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'row',
      flexShrink: 0,
      bgcolor: 'background.paper',
      borderBottom: frameBorder
    }),
    [frameBorder]
  );

  const renderDayHeaders = () =>
    dates.map((date) => (
      <Box
        key={`hdr-${date}`}
        sx={{
          width: dayColPx,
          minWidth: dayColPx,
          maxWidth: dayColPx,
          flexShrink: 0,
          ...headerCellSx,
          flexDirection: 'column',
          bgcolor: isWeekendDate(date) ? weekendHeaderBg(theme) : 'background.paper',
          ...dayDividerSx,
          px: 0.25,
          gap: 0.25
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '0.58rem', sm: '0.65rem', md: '0.7rem' },
            textAlign: 'center',
            lineHeight: 1.1
          }}
          color="text.secondary"
        >
          {format(parseISO(date), 'dd.MM')}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '0.72rem', sm: '0.8rem', md: '0.875rem' },
            textAlign: 'center',
            lineHeight: 1.15
          }}
        >
          {formatCalendarWeekdayLt(date, { short: dayNameShort })}
        </Typography>
      </Box>
    ));

  const renderHeaderBand = (dayScrollRef) => (
    <>
      <Box
        sx={{
          width: timeColPx,
          minWidth: timeColPx,
          flexShrink: 0,
          ...headerCellSx,
          borderBottom: 'none',
          ...timeColumnDividerSx
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, fontSize: { xs: '0.55rem', sm: '0.875rem', md: '1rem' } }}
        >
          Laikas
        </Typography>
      </Box>
      <Box
        ref={dayScrollRef}
        sx={{
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          pointerEvents: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: dates.length * dayColPx,
            minWidth: dates.length * dayColPx
          }}
        >
          {renderDayHeaders()}
        </Box>
      </Box>
    </>
  );

  const outerSx = fullBleed
    ? {
        width: { xs: '95vw', md: '87vw' },
        maxWidth: { xs: '95vw', md: '87vw' },
        mx: 'auto'
      }
    : { width: '100%' };

  const shellHeightSx = fillContainer
    ? { flex: 1, minHeight: 0 }
    : {
        height: {
          xs: `calc(100dvh - ${heightOffset.xs}px)`,
          sm: `calc(100dvh - ${heightOffset.sm}px)`,
          md: `calc(100dvh - ${heightOffset.md}px)`
        },
        minHeight: 240
      };

  return (
    <Box
      ref={outerRef}
      sx={{
        ...outerSx,
        ...(fillContainer ? { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' } : {})
      }}
    >
      <Box ref={sentinelRef} sx={{ height: 1, flexShrink: 0 }} aria-hidden />

      {/* In-flow header — fades out as fixed clone fades in */}
      <Box
        sx={{
          ...headerBandSx,
          height: headerPx,
          opacity: 1 - pinProgress,
          border: frameBorder,
          borderRadius: '8px 8px 0 0',
          willChange: 'opacity'
        }}
      >
        {renderHeaderBand(headerHScrollRef)}
      </Box>

      {/* Fixed header — scroll-driven crossfade below site nav */}
      {fixedFrame.width > 0 && (
        <Box
          sx={{
            ...headerBandSx,
            position: 'fixed',
            top: `${stickyTopPx}px`,
            left: `${fixedFrame.left}px`,
            width: `${fixedFrame.width}px`,
            height: headerPx,
            zIndex: 1100,
            opacity: pinProgress,
            transform: `translateY(${(1 - pinProgress) * -6}px)`,
            boxShadow: `0 ${pinProgress * 2}px ${pinProgress * 8}px rgba(0,0,0,${pinProgress * 0.08})`,
            border: frameBorder,
            pointerEvents: pinProgress > 0.5 ? 'auto' : 'none',
            willChange: 'opacity, transform, box-shadow'
          }}
        >
          {renderHeaderBand(fixedHeaderHScrollRef)}
        </Box>
      )}

      <Box
        sx={{
          ...shellHeightSx,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          border: frameBorder,
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row' }}>
          <Box
            sx={{
              width: timeColPx,
              minWidth: timeColPx,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              ...timeColumnDividerSx,
              zIndex: 2
            }}
          >
            <Box
              ref={timeScrollRef}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                pointerEvents: 'none',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' }
              }}
            >
              {timeSlots.map((time, index) => {
                const isHourEnd = (index + 1) % 2 === 0;
                const isHalfHour = time.includes(':30');
                return (
                  <Box
                    key={time}
                    sx={{
                      height: slotHeightPx,
                      minHeight: slotHeightPx,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                      color: 'text.secondary',
                      mb: isHourEnd && index < timeSlots.length - 1 ? 0.25 : 0,
                      borderBottom: slotBorder
                    }}
                  >
                    {!isHalfHour ? time : ''}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Box
              ref={hScrollRef}
              onScroll={handleHorizontalScroll}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
                scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
                '&::-webkit-scrollbar': { height: 10 },
                '&::-webkit-scrollbar-track': { bgcolor: 'grey.100' },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 5 }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: dates.length * dayColPx,
                  minWidth: dates.length * dayColPx,
                  height: '100%'
                }}
              >
                <Box
                  ref={vScrollRef}
                  onScroll={handleBodyScroll}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
                    '&::-webkit-scrollbar': { width: 10 },
                    '&::-webkit-scrollbar-track': { bgcolor: 'grey.100' },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 5 }
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    {dates.map((date) => (
                      <Box
                        key={date}
                        sx={{
                          width: dayColPx,
                          minWidth: dayColPx,
                          maxWidth: dayColPx,
                          flexShrink: 0,
                          ...dayDividerSx
                        }}
                      >
                        {timeSlots.map((time, index) => renderSlotCell(date, time, index))}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ScrollableBookingCalendar;
